import unified from "unified";
import remarkParse from "remark-parse";
import remarkGfm from "remark-gfm";

export interface ProjectRequirementSummary {
  imageCount: number;
  text: string;
}

function looksLikeLegacyRequirementHtml(requirement: string) {
  return /<\/?(?:p|div|br|img|strong|b|em|i|ul|ol|li|h[1-6]|blockquote|a)\b/i.test(
    requirement
  );
}

function legacyNodeToMarkdown(node: Node): string {
  if (node.nodeType === Node.TEXT_NODE) {
    return node.textContent || "";
  }
  if (node.nodeType !== Node.ELEMENT_NODE) {
    return "";
  }
  const element = node as HTMLElement;
  const tagName = element.tagName.toLowerCase();
  const children = Array.from(element.childNodes)
    .map(legacyNodeToMarkdown)
    .join("");
  if (tagName === "img") {
    const image = element as HTMLImageElement;
    const src = image.getAttribute("src") || "";
    return src ? `![${image.alt || ""}](${src})` : "";
  }
  if (tagName === "br") {
    return "\n";
  }
  if (tagName === "strong" || tagName === "b") {
    return `**${children}**`;
  }
  if (tagName === "em" || tagName === "i") {
    return `*${children}*`;
  }
  if (tagName === "a") {
    const href = element.getAttribute("href") || "";
    return href ? `[${children || href}](${href})` : children;
  }
  if (/^h[1-6]$/.test(tagName)) {
    return `${"#".repeat(Number(tagName.slice(1)))} ${children}\n\n`;
  }
  if (tagName === "blockquote") {
    return `${children
      .split("\n")
      .map((line) => `> ${line}`)
      .join("\n")}\n\n`;
  }
  if (tagName === "li") {
    return `- ${children.trim()}\n`;
  }
  if (["p", "div", "ul", "ol"].includes(tagName)) {
    return `${children.trim()}\n\n`;
  }
  return children;
}

export function normalizeProjectRequirementMarkdown(requirement?: string) {
  const normalized = String(requirement || "").trim();
  if (!normalized || !looksLikeLegacyRequirementHtml(normalized)) {
    return normalized;
  }
  const wrapper = document.createElement("div");
  wrapper.innerHTML = normalized;
  return Array.from(wrapper.childNodes)
    .map(legacyNodeToMarkdown)
    .join("")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

export function getProjectRequirementSummary(
  requirement?: string
): ProjectRequirementSummary {
  const normalized = normalizeProjectRequirementMarkdown(requirement);
  if (!normalized) {
    return { imageCount: 0, text: "" };
  }
  try {
    const tree = unified().use(remarkParse).use(remarkGfm).parse(normalized) as any;
    const textParts: string[] = [];
    let imageCount = 0;
    const blockTypes = new Set([
      "blockquote",
      "code",
      "heading",
      "list",
      "listItem",
      "paragraph",
      "table",
      "tableCell",
      "tableRow",
    ]);
    const visit = (node: any) => {
      if (!node) {
        return;
      }
      if (node.type === "image" || node.type === "imageReference") {
        imageCount += 1;
        if (node.alt) {
          textParts.push(String(node.alt));
        }
        textParts.push(" ");
        return;
      }
      if (["text", "inlineCode", "code"].includes(node.type) && node.value) {
        textParts.push(String(node.value));
        return;
      }
      if (["break", "thematicBreak"].includes(node.type)) {
        textParts.push(" ");
        return;
      }
      if (["definition", "html", "yaml"].includes(node.type)) {
        return;
      }
      (node.children || []).forEach(visit);
      if (blockTypes.has(node.type)) {
        textParts.push(" ");
      }
    };
    visit(tree);
    return {
      imageCount,
      text: textParts.join("").replace(/\s+/g, " ").trim(),
    };
  } catch {
    return {
      imageCount: 0,
      text: normalized.replace(/\s+/g, " ").trim(),
    };
  }
}

export function formatProjectRequirementSummary(requirement?: string) {
  const { imageCount, text } = getProjectRequirementSummary(requirement);
  if (!text && !imageCount) {
    return "";
  }
  if (!imageCount) {
    return text;
  }
  return text ? `${text} · ${imageCount} 张图片` : `${imageCount} 张需求图片`;
}
